"use client"

import {useState, useEffect, useRef, useCallback} from "react"
import {
    X,
    Copy,
    Link2,
    UserPlus,
    Globe,
    RefreshCw,
    Share2,
    Trash2,
    AlertTriangle,
    ChevronDown,
    Plus,
    Lock,
    Check
} from "lucide-react"
import {toast} from "sonner"
import {api, type ResourceAccessItem, type UserSearchItem} from "@/lib/api"
import {useAuth} from "@/lib/auth-context"
import {PublicLinkConfirmDialog} from "./public-link-confirm-dialog"
import {useThemeMode} from "@/lib/themes/theme-mode"
import {dashboardThemeVars} from "@/lib/themes/dashboard-theme-vars"
import {OverlayIcon} from "@/components/common/overlay-icon";

const AVATAR_COLORS = [
    "#60a5fa", "#f87171", "#4ade80", "#c084fc",
    "#fb923c", "#34d399", "#f472b6", "#94a3b8",
]

function avatarColor(id: string) {
    let h = 0
    for (let i = 0; i < id.length; i++) h = id.charCodeAt(i) + ((h << 5) - h)
    return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

function initials(name: string) {
    return name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
}

function firstName(name: string) {
    return name.split(" ")[0]
}

const POPUP_W = 540
const POPUP_H = 680


interface Props {
    resourceId: string
    resourceName: string
    resourceKind: "folder" | "file"
    iconSrc: string
    onClose: () => void
    ownerId?: string
    /** The top-level project this resource lives in — falls back to resourceId when the resource
     * IS the project root (e.g. editor-header's current project, or a top-level dashboard item). */
    projectId?: string
    /** Ancestor folder names between the project root and this resource, root-first — empty for a
     * top-level item. resourceName is appended to build the link's full path. */
    folderPath?: string[]
}

export function SharingManagePopup({
                                       resourceId,
                                       resourceName,
                                       resourceKind,
                                       iconSrc,
                                       onClose,
                                       ownerId: propsOwnerId,
                                       projectId,
                                       folderPath
                                   }: Props) {
    const {userId, profile} = useAuth()
    const {theme} = useThemeMode()
    const [pos, setPos] = useState(() => ({
        x: Math.max(0, window.innerWidth - POPUP_W - 16),
        y: 16,
    }))
    const [isDragging, setIsDragging] = useState(false)
    const dragRef = useRef<{ mx: number; my: number; px: number; py: number } | null>(null)
    const popupRef = useRef<HTMLDivElement>(null)
    const ownerFetchAttempted = useRef(false)

    const [accessItems, setAccessItems] = useState<ResourceAccessItem[]>([])
    const [copiedUrl, setCopiedUrl] = useState(false)
    const [ownerId, setOwnerId] = useState<string | null>(propsOwnerId || null)
    const [currentUserRole, setCurrentUserRole] = useState<"owner" | "editor" | "read" | null>(null)
    const [hasPublicLink, setHasPublicLink] = useState(false)
    const [publicLinks, setPublicLinks] = useState<{ read?: string; editor?: string }>({})

    const sharePath = [...(folderPath ?? []), resourceName].map(encodeURIComponent).join("/")
    const shareUrl = `${window.location.origin}/drive/privateurl/${projectId ?? resourceId}/${sharePath}`

    // Get current user ID from auth context
    const currentUserId = userId || profile?.id
    const isOwner = currentUserId && ownerId ? currentUserId === ownerId : false
    const canManageSharing = isOwner

    // Debug: log if ownerId and currentUserId for troubleshooting
    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            console.debug(`[Sharing] resourceId=${resourceId}, currentUserId=${currentUserId}, ownerId=${ownerId}, isOwner=${isOwner}`)
        }
    }, [resourceId, currentUserId, ownerId, isOwner])

    // User search state
    const [addQuery, setAddQuery] = useState("")
    const [searchResults, setSearchResults] = useState<UserSearchItem[]>([])
    const [searching, setSearching] = useState(false)
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Per-row action state
    const [removingId, setRemovingId] = useState<string | null>(null)
    const [addingId, setAddingId] = useState<string | null>(null)
    const [openAddMenuId, setOpenAddMenuId] = useState<string | null>(null)
    const addMenuRef = useRef<HTMLDivElement>(null)

    // Drag-and-drop state (shared between PermissionSections)
    const [draggedUser, setDraggedUser] = useState<{ userId: string; fromRole: "read" | "editor" } | null>(null)
    const [isDragOverSection, setIsDragOverSection] = useState<"read" | "editor" | null>(null)
    const [updatingRoleId, setUpdatingRoleId] = useState<string | null>(null)

    const refreshAccess = useCallback(async () => {
        const res = await api.getResourceAccess(resourceId)
        if (res.success) {
            setAccessItems(res.data.items ?? [])
            const links: { read?: string; editor?: string } = {}
            if (res.data.public_read_link?.path) {
                links.read = `${window.location.origin}${res.data.public_read_link.path}`
            }
            if (res.data.public_edit_link?.path) {
                links.editor = `${window.location.origin}${res.data.public_edit_link.path}`
            }
            setPublicLinks(links)
        }
    }, [resourceId])

    useEffect(() => {
        const fetchResourceData = async () => {
            // Refresh access list
            refreshAccess().catch(() => {
            })

            // Only fetch owner info if not already provided
            if (ownerId || ownerFetchAttempted.current) return

            ownerFetchAttempted.current = true
            try {
                // Try to get resource owner info from projects list
                const projectsRes = await api.getProjects()
                if (projectsRes.success && projectsRes.data) {
                    const resource = projectsRes.data.find(p => p.id === resourceId)
                    if (resource) {
                        setOwnerId(resource.owner_id)
                        return
                    }
                }
                // Fallback: if current user is authenticated, assume they're the owner
                // (they're the one editing/opening this resource in the editor)
                if (currentUserId) {
                    setOwnerId(currentUserId)
                }
            } catch { /* ignore */
            }
        }
        fetchResourceData()
    }, [resourceId, ownerId, currentUserId, refreshAccess])

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose()
        }
        document.addEventListener("keydown", onKey)
        return () => document.removeEventListener("keydown", onKey)
    }, [onClose])

    // Close "Add as" menu on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (addMenuRef.current && !addMenuRef.current.contains(e.target as Node)) {
                setOpenAddMenuId(null)
            }
        }
        document.addEventListener("mousedown", handler)
        return () => document.removeEventListener("mousedown", handler)
    }, [])

    // Debounced search
    const handleQueryChange = (val: string) => {
        setAddQuery(val)
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
        if (!val.trim()) {
            setSearchResults([]);
            return
        }
        searchTimerRef.current = setTimeout(async () => {
            setSearching(true)
            try {
                const res = await api.searchUsers(val.trim())
                if (res.success) setSearchResults(res.data.items)
            } catch { /* ignore */
            } finally {
                setSearching(false)
            }
        }, 300)
    }

    const handleRemove = async (userId: string) => {
        if (!canManageSharing) {
            toast.error("Only the owner can remove users")
            return
        }
        setRemovingId(userId)
        try {
            await api.removeShare(resourceId, userId)
            setAccessItems(prev => prev.filter(u => u.user_id !== userId))
        } catch { /* error toast from api */
        } finally {
            setRemovingId(null)
        }
    }

    const handleAddAs = async (user: UserSearchItem, role: "read" | "editor") => {
        if (!canManageSharing) {
            toast.error("Only the owner can add users")
            return
        }
        setAddingId(user.user_id)
        setOpenAddMenuId(null)
        try {
            await api.shareResource(resourceId, {role, shared_with: [user.user_id]})
            await refreshAccess()
        } catch { /* error toast from api */
        } finally {
            setAddingId(null)
        }
    }

    const handleDragStartUser = (userId: string, fromRole: "read" | "editor") => {
        setDraggedUser({userId, fromRole})
    }

    const handleDragEndUser = () => {
        setDraggedUser(null)
        setIsDragOverSection(null)
    }

    const handleDropOnSection = async (targetRole: "read" | "editor") => {
        if (!draggedUser || draggedUser.fromRole === targetRole || updatingRoleId) {
            setIsDragOverSection(null)
            return
        }

        const {userId, fromRole} = draggedUser
        const userItem = accessItems.find(u => u.user_id === userId)
        const userName = userItem?.name || "User"

        setUpdatingRoleId(userId)
        setDraggedUser(null)
        setIsDragOverSection(null)

        try {
            await api.shareResource(resourceId, {role: targetRole, shared_with: [userId]})
            await refreshAccess()

            const roleLabel = targetRole === "read" ? "Read-only" : "Edit"
            toast.success(`${userName} permission changed to ${roleLabel}`)
        } catch {
            toast.error("Failed to update permission")
        } finally {
            setUpdatingRoleId(null)
        }
    }

    const removeFromList = useCallback((userId: string) => {
        setAccessItems(prev => prev.filter(u => u.user_id !== userId))
    }, [])

    const openInNewWindow = useCallback(() => {
        localStorage.setItem("rudrise-sharing-detached", JSON.stringify({
            resourceId,
            resourceName,
            resourceKind,
            iconSrc
        }))
        window.open(
            `/share/${resourceId}?name=${encodeURIComponent(resourceName)}&kind=${resourceKind}`,
            `sharing-${resourceId}`,
            `width=${POPUP_W},height=${POPUP_H},menubar=no,toolbar=no,location=no,status=no,resizable=yes`,
        )
        onClose()
    }, [resourceId, resourceName, resourceKind, iconSrc, onClose])

    const onTitleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        setIsDragging(true)
        dragRef.current = {mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y}
    }, [pos])

    useEffect(() => {
        if (!isDragging) return
        const onMove = (e: MouseEvent) => {
            if (!dragRef.current) return
            if (e.clientX < 0 || e.clientX > window.innerWidth || e.clientY < 0 || e.clientY > window.innerHeight) {
                setIsDragging(false);
                dragRef.current = null;
                openInNewWindow();
                return
            }
            setPos({
                x: dragRef.current.px + (e.clientX - dragRef.current.mx),
                y: dragRef.current.py + (e.clientY - dragRef.current.my)
            })
        }
        const onUp = () => {
            setIsDragging(false);
            dragRef.current = null
        }
        document.addEventListener("mousemove", onMove)
        document.addEventListener("mouseup", onUp)
        return () => {
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp)
        }
    }, [isDragging, openInNewWindow])

    const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        return () => {
            if (copyTimerRef.current) clearTimeout(copyTimerRef.current)
        }
    }, [])

    const copyUrl = () => {
        navigator.clipboard.writeText(shareUrl).then(() => {
            setCopiedUrl(true)
            if (copyTimerRef.current) clearTimeout(copyTimerRef.current)
            copyTimerRef.current = setTimeout(() => setCopiedUrl(false), 3000)
        })
    }

    const handleNativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({title: resourceName, url: shareUrl})
            } catch { /* user cancelled */
            }
        } else {
            navigator.clipboard.writeText(shareUrl)
        }
    }

    // Derived search lists
    const q = addQuery.toLowerCase().trim()
    const isQuerying = q.length > 0
    const accessUserIds = new Set(accessItems.map(u => u.user_id))
    const accessMatches = isQuerying
        ? accessItems.filter(u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
        : []
    const newUsers = searchResults.filter(u => !accessUserIds.has(u.user_id))

    const readGroup = accessItems.filter(u => u.role === "read")
    const editorGroup = accessItems.filter(u => u.role === "editor")

    const iconColor =
        theme.name === "dark" || theme.name === "gray" ? "#000000" : "#ffffff"
    const dividerColor =
        theme.name === "dark" || theme.name === "gray"
            ? "rgba(0, 0, 0, 0.18)"
            : "rgba(255, 255, 255, 0.2)"

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-[9998]"
                style={{backgroundColor: "rgba(0, 0, 0, 0.55)"}}
                onMouseDown={onClose}
            />

            {/* Popup */}
            <div
                ref={popupRef}
                className="fixed z-[9999] bg-[var(--db-dialog-bg)] rounded-3xl shadow-2xl flex flex-col border border-[var(--db-border)]"
                style={{
                    left: pos.x, top: pos.y, width: POPUP_W,
                    height: "calc(100vh - 32px)",
                    userSelect: isDragging ? "none" : undefined,
                    fontFamily: "var(--font-inter, Inter, sans-serif)",
                    ...dashboardThemeVars(theme),
                }}
            >
                {/* Drag-handle title bar */}
                <div
                    className={`flex items-center justify-between px-6 pt-4 pb-2 shrink-0 ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
                    onMouseDown={onTitleMouseDown}
                >
                    <h2 className="text-[20px] font-semibold text-[var(--db-user-header)]">Sharing</h2>
                    <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[var(--db-surface-hover)] text-[var(--db-dialog-text-muted)] hover:text-[var(--db-dialog-text)] transition-colors"
                    >
                        <X className="w-5 h-5"/>
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto px-6 pb-5"
                     style={{scrollbarWidth: "none", overscrollBehavior: "contain"}}>
                    {/* Icon */}
                    <div className="flex items-center justify-center py-3">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <OverlayIcon
                            src={iconSrc} alt={resourceKind} draggable={false}
                            width={resourceKind === "folder" ? 110 : 88}
                            height={resourceKind === "folder" ? 90 : 88}
                            style={{objectFit: "contain"}}
                        />
                    </div>

                    <hr className="border-[var(--db-border)] mb-3"/>

                    <h3 className="text-[18px] font-semibold text-[var(--db-dialog-text)] mb-0.5">{resourceName}</h3>
                    <p className="text-[12px] text-[var(--db-dialog-text-muted)] mb-3">{resourceKind === "folder" ? "Folder" : "File"}</p>

                    {/* Share URL */}
                    <div
                        className="flex items-center border border-[var(--db-border)] overflow-hidden mb-1"
                        style={{ borderRadius: 4 }}
                    >
  <span
      className="flex-1 px-3 text-[13px] text-[var(--db-user-header)] truncate"
      style={{ lineHeight: "18px", padding: "5px 12px" }}
  >
    {shareUrl}
  </span>
                        <div
                            className="flex items-center shrink-0 self-stretch bg-[var(--db-action-bar-bg)] border-l border-[var(--db-border)]"
                        >
                            {/* Copy link */}
                            <button
                                type="button"
                                onClick={copyUrl}
                                className={`flex items-center justify-center px-2.5 self-stretch transition-colors border-r ${
                                    copiedUrl
                                        ? "bg-green-500"
                                        : "hover:bg-white/10 dark:hover:bg-black/10"
                                }`}
                                style={{ borderColor: dividerColor }}
                                title={copiedUrl ? "Copied!" : "Copy link"}
                            >
                                {copiedUrl ? (
                                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                                ) : (
                                    <span
                                        aria-hidden="true"
                                        className="w-3 h-3 shrink-0 inline-block pointer-events-none"
                                        style={{
                                            backgroundColor: iconColor,
                                            WebkitMask: "url(/ic_popup_copy.svg) no-repeat center / contain",
                                            mask: "url(/ic_popup_copy.svg) no-repeat center / contain",
                                        }}
                                    />
                                )}
                            </button>

                            {/* Native share */}
                            <button
                                type="button"
                                onClick={handleNativeShare}
                                className="flex items-center justify-center px-2.5 self-stretch hover:bg-white/10 dark:hover:bg-black/10 transition-colors"
                                title="Share link"
                            >
      <span
          aria-hidden="true"
          className="w-3 h-3 shrink-0 inline-block pointer-events-none"
          style={{
              backgroundColor: iconColor,
              WebkitMask: "url(/ic_popup_share.svg) no-repeat center / contain",
              mask: "url(/ic_popup_share.svg) no-repeat center / contain",
          }}
      />
                            </button>
                        </div>
                    </div>
                    {copiedUrl && <p className="text-[11px] text-green-500 mb-2 px-1">Link copied!</p>}

                    <hr className="mt-2 border-[var(--db-border)]"/>

                    {/* Search input — only show to owner */}
                    {canManageSharing ? (
                        <div className="flex items-center gap-2 border border-[var(--db-border)] px-3 mt-2 mb-3"
                             style={{borderRadius: 4, paddingTop: 5, paddingBottom: 5}}>
                            <UserPlus className="w-4 h-4 text-[var(--db-dialog-text-muted)] shrink-0"/>
                            <input
                                type="text"
                                placeholder="Enter name, username or email"
                                value={addQuery}
                                onChange={(e) => handleQueryChange(e.target.value)}
                                autoComplete="off"
                                className="flex-1 text-[13px] outline-none bg-transparent placeholder-[var(--db-dialog-text-muted)] text-[var(--db-dialog-text)]"
                                style={{lineHeight: "18px"}}
                            />
                            {searching && (
                                <div
                                    className="w-3.5 h-3.5 border-2 border-[var(--db-border)] border-t-[var(--db-user-header)] rounded-full animate-spin shrink-0"/>
                            )}
                            {addQuery && !searching && (
                                <button
                                    type="button"
                                    onClick={() => handleQueryChange("")}
                                    className="flex items-center justify-center w-4 h-4 rounded-full bg-[var(--db-border)] hover:opacity-80 transition-opacity shrink-0"
                                    title="Clear"
                                >
                                    <X className="w-2.5 h-2.5 text-[var(--db-dialog-text)]"/>
                                </button>
                            )}
                        </div>
                    ) : (
                        <div
                            className="flex items-center gap-2 p-3 mt-2 mb-3 bg-[var(--db-warn-bg)] border border-[var(--db-warn-border)] rounded-lg">
                            <Lock className="w-4 h-4 text-amber-500 shrink-0"/>
                            <p className="text-[12px] text-amber-500 dark:text-amber-300">Only the data owner can manage
                                data share access</p>
                        </div>
                    )}

                    {/* ── Search results list — only if owner ── */}
                    {canManageSharing && isQuerying ? (
                        <div>
                            {/* Access users that match */}
                            {accessMatches.map(u => (
                                <UserRow key={u.user_id}>
                                    <UserAvatar id={u.user_id} name={u.name}/>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span
                                                className="text-[14px] font-semibold text-[var(--db-dialog-text)] truncate">{u.name}</span>
                                            <RoleBadge role={u.role}/>
                                        </div>
                                        <p className="text-[12px] text-[var(--db-dialog-text-muted)] truncate">@{u.email}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRemove(u.user_id)}
                                        disabled={removingId === u.user_id}
                                        className="shrink-0 px-3 py-1 text-[12px] font-medium text-red-500 border border-red-400 hover:bg-red-50 transition-colors disabled:opacity-50"
                                        style={{borderRadius: 4}}
                                    >
                                        {removingId === u.user_id ? "…" : "Remove"}
                                    </button>
                                </UserRow>
                            ))}

                            {/* New users from API */}
                            {newUsers.map(u => (
                                <UserRow key={u.user_id}>
                                    <UserAvatar id={u.user_id} name={u.name}/>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[14px] font-semibold text-[var(--db-dialog-text)] truncate">{u.name}</p>
                                        <p className="text-[12px] text-[var(--db-dialog-text-muted)] truncate">@{u.email}</p>
                                    </div>
                                    {/* Add as button */}
                                    <div className="relative shrink-0"
                                         ref={openAddMenuId === u.user_id ? addMenuRef : undefined}>
                                        <div
                                            style={{borderRadius: 4}}
                                            className="flex items-center overflow-hidden bg-[var(--db-user-header)] text-[var(--db-button-text)]"
                                        >
                                            <button
                                                type="button"
                                                disabled={addingId === u.user_id}
                                                onClick={() => setOpenAddMenuId(id => id === u.user_id ? null : u.user_id)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-[var(--db-button-text)] hover:opacity-90 transition-opacity disabled:opacity-50"
                                            >
                                                <Plus className="w-3 h-3"/>
                                                {addingId === u.user_id ? "Adding…" : "Add as"}
                                            </button>
                                            <div className="w-px self-stretch bg-black/20 dark:bg-white/20"/>
                                            <button
                                                type="button"
                                                onClick={() => setOpenAddMenuId(id => id === u.user_id ? null : u.user_id)}
                                                className="px-2 py-1.5 hover:opacity-90 transition-opacity text-[var(--db-button-text)]"
                                            >
                                                <ChevronDown className="w-3.5 h-3.5"/>
                                            </button>
                                        </div>

                                        {/* Role dropdown */}
                                        {openAddMenuId === u.user_id && (
                                            <div
                                                className="absolute right-0 bottom-full mb-1 bg-[var(--db-dialog-bg)] border border-[var(--db-border)] shadow-lg z-20 py-1 overflow-hidden"
                                                style={{borderRadius: 4, minWidth: 160}}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => handleAddAs(u, "read")}
                                                    className="w-full text-left px-4 py-2 text-[13px] text-[var(--db-dialog-text)] hover:bg-[var(--db-surface-hover)] transition-colors"
                                                >
                                                    Add as Read-only
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleAddAs(u, "editor")}
                                                    className="w-full text-left px-4 py-2 text-[13px] text-[var(--db-dialog-text)] hover:bg-[var(--db-surface-hover)] transition-colors"
                                                >
                                                    Add as Editor
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </UserRow>
                            ))}

                            {accessMatches.length === 0 && newUsers.length === 0 && !searching && (
                                <p className="text-[13px] text-[var(--db-dialog-text-muted)] py-3">No users found.</p>
                            )}
                        </div>
                    ) : (
                        /* ── Default: avatar grid per permission group ── */
                        <>
                            <hr className="border-[var(--db-border)] mb-3"/>
                            {accessItems.length === 0 && (
                                <p className="text-[13px] text-[var(--db-dialog-text-muted)] mb-3">Not shared with
                                    anyone yet.</p>
                            )}
                            {/* Generating a public link doesn't require anyone to already have that permission —
                these render regardless of group size so Read/Edit links are always available. */}
                            <PermissionSection
                                label="Read-only permission"
                                role="read"
                                users={readGroup}
                                resourceId={resourceId}
                                existingPublicLink={publicLinks.read}
                                onRemove={removeFromList}
                                draggedUser={draggedUser}
                                isDragOver={isDragOverSection === "read"}
                                updatingRoleId={updatingRoleId}
                                onDragStart={handleDragStartUser}
                                onDragEnd={handleDragEndUser}
                                onDrop={() => handleDropOnSection("read")}
                                onDragOverChange={(isOver) => setIsDragOverSection(isOver ? "read" : null)}
                                canManageSharing={canManageSharing}
                                onLinkGenerated={() => refreshAccess()}
                            />
                            <PermissionSection
                                label="Edit permission"
                                role="editor"
                                users={editorGroup}
                                resourceId={resourceId}
                                existingPublicLink={publicLinks.editor}
                                onRemove={removeFromList}
                                draggedUser={draggedUser}
                                isDragOver={isDragOverSection === "editor"}
                                updatingRoleId={updatingRoleId}
                                onDragStart={handleDragStartUser}
                                onDragEnd={handleDragEndUser}
                                onDrop={() => handleDropOnSection("editor")}
                                onDragOverChange={(isOver) => setIsDragOverSection(isOver ? "editor" : null)}
                                canManageSharing={canManageSharing}
                                onLinkGenerated={() => refreshAccess()}
                            />
                        </>
                    )}
                </div>
            </div>
        </>
    )
}

/* ── Small helpers ── */
function UserRow({children}: { children: React.ReactNode }) {
    return (
        <div className="flex items-center gap-3 py-2.5 border-b border-[var(--db-border)] last:border-0">
            {children}
        </div>
    )
}

function UserAvatar({id, name}: { id: string; name: string }) {
    return (
        <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0"
            style={{backgroundColor: avatarColor(id)}}
        >
            {initials(name)}
        </div>
    )
}

function RoleBadge({role}: { role: "read" | "editor" }) {
    return role === "editor" ? (
        <span
            className="px-2 py-0.5 text-[10px] font-medium text-[var(--db-button-text)] bg-[var(--db-user-header)] rounded-sm">Editor</span>
    ) : (
        <span className="px-2 py-0.5 text-[10px] font-medium text-[var(--db-dialog-text-muted)]"
              style={{backgroundColor: "var(--db-tag-bg, #E5E7EB)", borderRadius: 2}}>Read-only</span>
    )
}

/* ── Permission section (avatar grid + public link) ── */
function PermissionSection({
                               label, role, users, resourceId, onRemove,
                               draggedUser, isDragOver, updatingRoleId,
                               onDragStart, onDragEnd, onDrop, onDragOverChange,
                               canManageSharing, existingPublicLink, onLinkGenerated,
                           }: {
    label: string
    role: "read" | "editor"
    users: ResourceAccessItem[]
    resourceId: string
    existingPublicLink?: string
    onRemove: (userId: string) => void
    draggedUser: { userId: string; fromRole: "read" | "editor" } | null
    isDragOver: boolean
    updatingRoleId: string | null
    onDragStart: (userId: string, fromRole: "read" | "editor") => void
    onDragEnd: () => void
    onDrop: () => void
    onDragOverChange: (isOver: boolean) => void
    canManageSharing: boolean
    onLinkGenerated?: () => void
}) {
    const [expanded, setExpanded] = useState(false)
    const [pendingRemove, setPendingRemove] = useState<Record<string, "loading" | "done">>({})

    const PER_ROW = 8
    const MAX_SHOWN = PER_ROW * 2
    const hasExtra = !expanded && users.length > MAX_SHOWN
    const shown = expanded ? users : users.slice(0, hasExtra ? MAX_SHOWN - 1 : MAX_SHOWN)
    const extra = hasExtra ? users.length - (MAX_SHOWN - 1) : 0

    const { theme } = useThemeMode()
    const iconColor =
        theme.name === "dark" || theme.name === "gray"
            ? "#000000"
            : "#ffffff"

    const handleRemoveClick = async (userId: string) => {
        if (pendingRemove[userId]) return
        setPendingRemove(p => ({...p, [userId]: "loading"}))
        try {
            await api.removeShare(resourceId, userId)
            setPendingRemove(p => ({...p, [userId]: "done"}))
            setTimeout(() => onRemove(userId), 420)
        } catch {
            setPendingRemove(p => {
                const n = {...p};
                delete n[userId];
                return n
            })
        }
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        e.dataTransfer!.dropEffect = "move"
        onDragOverChange(true)
    }

    const handleDragLeave = () => {
        onDragOverChange(false)
    }

    const handleDropLocal = (e: React.DragEvent) => {
        e.preventDefault()
        onDrop()
    }

    const [generating, setGenerating] = useState(false)
    const [copied, setCopied] = useState(false)
    const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        return () => {
            if (copyTimerRef.current) clearTimeout(copyTimerRef.current)
        }
    }, [])

    const warningText = role === "read"
        ? "A public link has been generated. Anyone with the link can view this file."
        : "A public link has been generated. Anyone with the link can edit this file."

    const generateLink = async () => {
        if (generating) return
        setGenerating(true)
        try {
            const res = await api.generatePublicLink(resourceId, {role})
            if (res.success && res.data) {
                toast.success("Public link generated")
                onLinkGenerated?.()
            } else {
                toast.error("Failed to generate link")
            }
        } catch (err) {
            console.error("Generate link error:", err)
            toast.error("Error generating link")
        } finally {
            setGenerating(false)
        }
    }

    const handleRegenerateConfirm = async () => {
        await generateLink()
        setShowRegenerateConfirm(false)
    }

    const handleDeleteConfirm = async () => {
        if (deleting) return
        setDeleting(true)
        try {
            await api.deletePublicLink(resourceId, {role})
            toast.success("Public link deleted")
            setShowDeleteConfirm(false)
            onLinkGenerated?.()
        } catch (err) {
            console.error("Delete link error:", err)
            toast.error("Error deleting link")
        } finally {
            setDeleting(false)
        }
    }

    const copyLink = () => {
        if (!existingPublicLink) return
        navigator.clipboard.writeText(existingPublicLink).then(() => {
            setCopied(true)
            if (copyTimerRef.current) clearTimeout(copyTimerRef.current)
            copyTimerRef.current = setTimeout(() => setCopied(false), 3000)
        })
    }
    const shareLink = async () => {
        if (!existingPublicLink) return
        const url = existingPublicLink
        if (navigator.share) {
            try {
                await navigator.share({url})
            } catch { /* cancelled */
            }
        } else {
            navigator.clipboard.writeText(url)
        }
    }

    return (
        <div className="mb-4">
            <p className="text-[14px] font-normal text-[var(--db-dialog-text)] mb-2"
               style={{fontFamily: "var(--font-inter, Inter, sans-serif)"}}>
                {label} <span className="font-normal text-[var(--db-dialog-text-muted)]">({users.length} Users)</span>
            </p>

            {!canManageSharing && (
                <div
                    className="flex items-center gap-2 p-2 mb-3 bg-[var(--db-tag-bg)] border border-[var(--db-border)] rounded-lg">
                    <Lock className="w-3.5 h-3.5 text-[var(--db-dialog-text-muted)] shrink-0"/>
                    <p className="text-[11px] text-[var(--db-dialog-text-muted)]">Link is visible to data owner only</p>
                </div>
            )}

            {canManageSharing ? (
                <>
                    {existingPublicLink ? (
                        <div className="mb-3">
                            <div className="flex items-center border border-[var(--db-border)] overflow-hidden"
                                 style={{borderRadius: 4}}>
                                <div className="flex items-center gap-2 flex-1 px-3 py-1.5 min-w-0">
                                    <Globe className="w-3.5 h-3.5 text-[var(--db-user-header)] shrink-0"/>
                                    <span
                                        className="text-[12px] text-[var(--db-user-header)] truncate">{existingPublicLink}</span>
                                </div>
                                <div
                                    className="flex items-center self-stretch shrink-0 bg-[var(--db-action-bar-bg)] border-l border-[var(--db-border)]"
                                >
                                    {/* Copy button */}
                                    <button
                                        type="button"
                                        onClick={copyLink}
                                        className={`flex items-center justify-center px-2.5 self-stretch transition-colors border-r border-white/20 ${
                                            copied ? "bg-green-500" : "hover:bg-white/10"
                                        }`}
                                        title={copied ? "Copied!" : "Copy"}
                                    >
                                        {copied ? (
                                            <Check className="w-3 h-3 text-white" strokeWidth={3} />
                                        ) : (
                                            <span
                                                aria-hidden="true"
                                                className="w-3 h-3 shrink-0 inline-block pointer-events-none"
                                                style={{
                                                    backgroundColor: iconColor,
                                                    WebkitMask: "url(/ic_popup_copy.svg) no-repeat center / contain",
                                                    mask: "url(/ic_popup_copy.svg) no-repeat center / contain",
                                                }}
                                            />
                                        )}
                                    </button>

                                    {/* Regenerate button */}
                                    <button
                                        type="button"
                                        onClick={() => setShowRegenerateConfirm(true)}
                                        className="flex items-center justify-center px-2.5 self-stretch hover:bg-white/10 transition-colors border-r border-white/20"
                                        title="Regenerate"
                                    >
                                        <RefreshCw
                                            className="w-3 h-3 shrink-0"
                                            style={{ color: iconColor }}
                                            strokeWidth={1.8}
                                        />
                                    </button>

                                    {/* Share button */}
                                    <button
                                        type="button"
                                        onClick={shareLink}
                                        className="flex items-center justify-center px-2.5 self-stretch hover:bg-white/10 transition-colors border-r border-white/20"
                                        title="Share"
                                    >
    <span
        aria-hidden="true"
        className="w-3 h-3 shrink-0 inline-block pointer-events-none"
        style={{
            backgroundColor: iconColor,
            WebkitMask: "url(/ic_popup_share.svg) no-repeat center / contain",
            mask: "url(/ic_popup_share.svg) no-repeat center / contain",
        }}
    />
                                    </button>

                                    {/* Revoke / Delete button */}
                                    <button
                                        type="button"
                                        onClick={() => setShowDeleteConfirm(true)}
                                        className="flex items-center justify-center px-2.5 self-stretch hover:bg-red-500/15 transition-colors"
                                        title="Revoke"
                                    >
  <span
      aria-hidden="true"
      className="w-3.5 h-3.5 shrink-0 inline-block pointer-events-none bg-red-500"
      style={{
          WebkitMask: "url(/ic_delete.svg) no-repeat center / contain",
          mask: "url(/ic_delete.svg) no-repeat center / contain",
      }}
  />
                                    </button>
                                </div>
                            </div>
                            {copied && <p className="text-[11px] text-green-500 mt-1 mb-1 px-1">Link copied!</p>}
                            <div className="flex items-center gap-1.5 mt-2">
                                <AlertTriangle className="w-3.5 h-3.5 text-orange-400 shrink-0"/>
                                <p className="text-[12px] text-orange-400 leading-none">{warningText}</p>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button" onClick={generateLink} disabled={generating}
                            className="flex items-center gap-1.5 border border-[var(--db-border)] px-4 py-1.5 text-[12px] text-[var(--db-dialog-text)] hover:bg-[var(--db-surface-hover)] mb-3 transition-colors disabled:opacity-50"
                            style={{borderRadius: 4}}
                        >
                            <Link2 className="w-3 h-3"/>
                            {generating ? "Generating…" : "Generate Public Link"}
                        </button>
                    )}
                </>
            ) : null}

            <div
                className={`flex flex-wrap gap-x-3 gap-y-3 p-2 rounded-lg transition-colors ${
                    isDragOver ? "bg-[var(--db-card-selected-bg)] border border-[var(--db-user-header)]" : ""
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDropLocal}
            >
                {shown.map((u) => {
                    const pending = pendingRemove[u.user_id]
                    const isUpdatingRole = updatingRoleId === u.user_id
                    return (
                        <div
                            key={u.user_id}
                            draggable
                            onDragStart={() => onDragStart(u.user_id, role)}
                            onDragEnd={onDragEnd}
                            className="flex flex-col items-center gap-1 group cursor-grab active:cursor-grabbing"
                            style={{
                                width: 48,
                                opacity: pending === "done" || isUpdatingRole ? 0 : 1,
                                transform: pending === "done" || isUpdatingRole ? "scale(0.5)" : "scale(1)",
                                transition: "opacity 0.35s ease, transform 0.35s ease",
                                pointerEvents: pending || isUpdatingRole ? "none" : undefined,
                            }}
                        >
                            <div className="relative">
                                <div
                                    className="w-10 h-10 rounded-full flex items-center justify-center text-[11px] font-bold text-white cursor-grab active:cursor-grabbing"
                                    style={{
                                        backgroundColor: pending || isUpdatingRole ? "#9CA3AF" : avatarColor(u.user_id),
                                        transition: "background-color 0.2s ease",
                                        opacity: draggedUser?.userId === u.user_id ? 0.5 : 1,
                                    }}
                                    title={`${u.name} · ${u.email}\n(Drag to change permission)`}
                                >
                                    {initials(u.name)}
                                </div>

                                {/* Circular progress overlay while removing or updating role */}
                                {(pending === "loading" || isUpdatingRole) && (
                                    <div
                                        className="absolute inset-0 rounded-full flex items-center justify-center"
                                        style={{background: "rgba(0,0,0,0.18)"}}
                                    >
                                        <div
                                            className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"/>
                                    </div>
                                )}

                                {/* Hover remove button — hidden while pending and only shown to owner */}
                                {!pending && canManageSharing && (
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveClick(u.user_id)}
                                        title={`Remove ${u.name}`}
                                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                    >
                                        <X className="w-2.5 h-2.5 text-[var(--db-dialog-text)]"/>
                                    </button>
                                )}
                            </div>
                            <span
                                className="text-[10px] text-[var(--db-dialog-text-muted)] truncate w-full text-center leading-tight">
                {firstName(u.name)}
              </span>
                        </div>
                    )
                })}
                {extra > 0 && (
                    <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        className="flex flex-col items-center gap-1"
                        style={{width: 48}}
                    >
                        <div
                            className="w-10 h-10 rounded-full flex items-center justify-center text-[11px] font-semibold hover:bg-[var(--db-surface-hover)] transition-colors"
                            style={{backgroundColor: "var(--db-tag-bg)", color: "var(--db-dialog-text-muted)"}}>
                            +{extra}
                        </div>
                        <span className="text-[10px] text-[var(--db-dialog-text-muted)] leading-tight">more</span>
                    </button>
                )}
            </div>

            {showRegenerateConfirm && (
                <PublicLinkConfirmDialog
                    variant="update"
                    loading={generating}
                    onConfirm={() => void handleRegenerateConfirm()}
                    onCancel={() => setShowRegenerateConfirm(false)}
                />
            )}
            {showDeleteConfirm && (
                <PublicLinkConfirmDialog
                    variant="delete"
                    loading={deleting}
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => setShowDeleteConfirm(false)}
                />
            )}
        </div>
    )
}
